import { useState, useEffect } from 'react'
import { Save, Search, Info, Sparkles, CalendarDays, Users, Building2, MapPin, Globe, CheckCircle2, User as UserIcon } from 'lucide-react'
import { weekOffAPI, employeesAPI, centersAPI, districtsAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import WeekOffDayGrid, { DayRule } from '../../components/WeekOffDayGrid'

type Tab = 'GLOBAL' | 'EMPLOYEE' | 'TRAINING_CENTRE' | 'DISTRICT'

const DAY_LABELS: Record<number, string> = {
  0: 'Monday', 1: 'Tuesday', 2: 'Wednesday', 3: 'Thursday', 4: 'Friday', 5: 'Saturday', 6: 'Sunday',
}
const PATTERN_LABELS: Record<string, string> = {
  EVERY_WEEK: 'every week', WEEK_1: '1st week', WEEK_2: '2nd week', WEEK_3: '3rd week', WEEK_4: '4th week', WEEK_5: '5th week',
}

function summarize(rules: DayRule[]): string {
  if (rules.length === 0) return 'No weekly offs configured.'
  const byDay: Record<number, string[]> = {}
  for (const r of rules) {
    if (!byDay[r.day_of_week]) byDay[r.day_of_week] = []
    byDay[r.day_of_week].push(PATTERN_LABELS[r.week_pattern] || r.week_pattern)
  }
  return Object.entries(byDay)
    .map(([day, patterns]) => `${DAY_LABELS[Number(day)]} (${patterns.join(', ')})`)
    .join(' · ')
}

const TAB_META: { key: Tab; label: string; icon: any }[] = [
  { key: 'GLOBAL', label: 'Global', icon: Globe },
  { key: 'EMPLOYEE', label: 'Employee-wise', icon: Users },
  { key: 'TRAINING_CENTRE', label: 'Training Centre', icon: Building2 },
  { key: 'DISTRICT', label: 'District', icon: MapPin },
]

export default function WeekOffConfigPage() {
  const { success, error: toastError } = useToast()
  const [tab, setTab] = useState<Tab>('GLOBAL')

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="mb-7">
        <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
          <Sparkles className="w-3 h-3" />
          Schedule
        </div>
        <h1
          className="text-3xl font-bold text-neutral-900 mb-1"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Weekly Off Configuration
        </h1>
        <p className="text-sm text-neutral-500 max-w-3xl">
          Weekly offs are <strong className="text-neutral-700 font-semibold">additive</strong> — an employee's
          effective days off combine every applicable level (global, their training centre, their district,
          and any personal override).
        </p>
      </div>

      {/* ── Tab bar ── */}
      <div className="mb-6 flex items-center gap-1.5 bg-white border border-neutral-200 rounded-2xl p-1.5 shadow-sm overflow-x-auto">
        {TAB_META.map(({ key, label, icon: Icon }) => {
          const active = tab === key
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`group flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                active
                  ? 'bg-gradient-to-r from-[#F0821F] to-[#E4670F] text-white shadow-md shadow-[#E4670F]/25'
                  : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
              }`}
            >
              <Icon
                className={`w-4 h-4 transition-transform ${
                  !active ? 'group-hover:scale-110' : ''
                }`}
              />
              {label}
            </button>
          )
        })}
      </div>

      {tab === 'GLOBAL' && <GlobalTab success={success} toastError={toastError} />}
      {tab === 'EMPLOYEE' && <EmployeeTab success={success} toastError={toastError} />}
      {tab === 'TRAINING_CENTRE' && <ScopeTab scope="TRAINING_CENTRE" success={success} toastError={toastError} />}
      {tab === 'DISTRICT' && <ScopeTab scope="DISTRICT" success={success} toastError={toastError} />}
    </div>
  )
}

// ── Shared: section card wrapper ─────────────────────────────────────────
function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
      <div className="p-6 sm:p-7">{children}</div>
    </div>
  )
}

// ── Shared: gradient info banner ─────────────────────────────────────────
function InfoBanner({ icon: Icon = Info, children }: { icon?: any; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-3.5 py-2.5 text-sm text-blue-800">
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  )
}

// ── Shared: gradient save button ─────────────────────────────────────────
function SaveButton({
  onClick, disabled, children,
}: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5 disabled:hover:translate-y-0"
    >
      <Save className="w-4 h-4 transition-transform group-hover:scale-110" />
      {children}
    </button>
  )
}

// ── Shared: loading state ────────────────────────────────────────────────
function LoadingState({ label = 'Loading...' }: { label?: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
      <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
      <p className="text-sm text-neutral-500">{label}</p>
    </div>
  )
}

// ── Global tab ────────────────────────────────────────────────────────────
function GlobalTab({ success, toastError }: { success: (m: string) => void; toastError: (m: string) => void }) {
  const [rules, setRules] = useState<DayRule[]>([])
  const [pendingRules, setPendingRules] = useState<DayRule[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const load = async () => {
    setIsLoading(true)
    try {
      const res = await weekOffAPI.list({ scope: 'GLOBAL' })
      const loaded = res.data.map((r: any) => ({ day_of_week: r.day_of_week, week_pattern: r.week_pattern }))
      setRules(loaded)
      setPendingRules(loaded)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load the global weekly-off configuration.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await weekOffAPI.setScope({ scope: 'GLOBAL', rules: pendingRules })
      success('Global weekly-off configuration saved.')
      await load()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save weekly-off configuration.'))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) return <LoadingState />

  return (
    <div className="max-w-3xl">
      <SectionCard>
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30 flex-shrink-0">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
              Global Default
            </h2>
            <p className="text-sm text-neutral-500 mt-0.5">
              Applies to every employee by default.
            </p>
          </div>
        </div>

        <InfoBanner>
          This organization's standard is <strong className="font-semibold">Saturday + Sunday</strong> —
          use the quick presets below if you need alternate-Saturday instead of every week.
        </InfoBanner>

        <div className="mt-5">
          <WeekOffDayGrid initialRules={rules} onChange={setPendingRules} />
        </div>

        <div className="mt-6 pt-5 border-t border-neutral-100">
          <SaveButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Global Configuration'}
          </SaveButton>
        </div>
      </SectionCard>
    </div>
  )
}

// ── Employee-wise tab ────────────────────────────────────────────────────
function EmployeeTab({ success, toastError }: { success: (m: string) => void; toastError: (m: string) => void }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null)

  const [rules, setRules] = useState<DayRule[]>([])
  const [pendingRules, setPendingRules] = useState<DayRule[]>([])
  const [effective, setEffective] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults([])
      return
    }
    const timeout = setTimeout(() => {
      employeesAPI.list(1, searchTerm).then((res) => setSearchResults(res.data.results || res.data)).catch(() => {})
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchTerm])

  const loadForEmployee = async (employee: any) => {
    setSelectedEmployee(employee)
    setSearchResults([])
    setSearchTerm('')
    setIsLoading(true)
    try {
      const [rulesRes, effectiveRes] = await Promise.all([
        weekOffAPI.list({ scope: 'EMPLOYEE', employee: employee.id }),
        weekOffAPI.getEffective(employee.id),
      ])
      const loaded = rulesRes.data.map((r: any) => ({ day_of_week: r.day_of_week, week_pattern: r.week_pattern }))
      setRules(loaded)
      setPendingRules(loaded)
      setEffective(effectiveRes.data)
    } catch (err: any) {
      toastError(getErrorMessage(err, "Failed to load this employee's weekly-off configuration."))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!selectedEmployee) return
    setIsSaving(true)
    try {
      await weekOffAPI.setScope({ scope: 'EMPLOYEE', employee: selectedEmployee.id, rules: pendingRules })
      success(`Weekly-off configuration saved for ${selectedEmployee.full_name}.`)
      await loadForEmployee(selectedEmployee)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save weekly-off configuration.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="max-w-3xl">
      {/* Employee picker */}
      <SectionCard>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
            <Search className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
              Select Employee
            </h2>
            <p className="text-xs text-neutral-500">Search by name or employee ID</p>
          </div>
        </div>

        <div className="relative group">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or employee ID..."
            value={selectedEmployee ? `${selectedEmployee.full_name} (${selectedEmployee.employee_id})` : searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setSelectedEmployee(null) }}
            className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
          />
        </div>

        {searchResults.length > 0 && (
          <div className="mt-3 bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-lg shadow-neutral-200/50 max-h-56 overflow-y-auto">
            {searchResults.map((emp) => (
              <button
                key={emp.id}
                onClick={() => loadForEmployee(emp)}
                className="group w-full flex items-center gap-3 text-left px-3.5 py-2.5 text-sm hover:bg-[#E4670F]/[0.06] transition-colors border-b border-neutral-100 last:border-b-0"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                  {emp.full_name?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-neutral-900 truncate group-hover:text-[#C24A0A]">
                    {emp.full_name}
                  </p>
                  <p className="text-xs text-neutral-500">{emp.employee_id}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Employee config */}
      {isLoading ? (
        <div className="mt-4"><LoadingState /></div>
      ) : selectedEmployee ? (
        <div className="mt-4">
          <SectionCard>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-sm font-bold text-white ring-2 ring-white shadow-sm flex-shrink-0">
                {selectedEmployee.full_name?.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-neutral-900 truncate" style={{ fontFamily: 'Georgia, serif' }}>
                  {selectedEmployee.full_name}
                </h2>
                <p className="text-xs text-neutral-500">{selectedEmployee.employee_id}</p>
              </div>
            </div>

            <InfoBanner>
              Additional weekly offs for <strong className="font-semibold">{selectedEmployee.full_name}</strong> —
              these <strong className="font-semibold">add to</strong> whatever their training centre, district,
              and global configuration already grant.
            </InfoBanner>

            <div className="mt-5">
              <WeekOffDayGrid initialRules={rules} onChange={setPendingRules} />
            </div>

            {effective.length > 0 && (
              <div className="mt-5 flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-3 text-sm text-emerald-800">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 mb-0.5">
                    Effective Weekly Offs (all scopes combined)
                  </p>
                  <p className="text-sm text-emerald-800">
                    {summarize(effective.map((r: any) => ({ day_of_week: r.day_of_week, week_pattern: r.week_pattern })))}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-neutral-100">
              <SaveButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Employee Configuration'}
              </SaveButton>
            </div>
          </SectionCard>
        </div>
      ) : (
        <div className="mt-4 bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 py-16 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <UserIcon className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No employee selected</p>
          <p className="text-xs text-neutral-400">Search for an employee above to configure their personal weekly offs.</p>
        </div>
      )}
    </div>
  )
}

// ── Training Centre-wise / District-wise tab (shared, parameterized) ──────
function ScopeTab({
  scope, success, toastError,
}: {
  scope: 'TRAINING_CENTRE' | 'DISTRICT'
  success: (m: string) => void
  toastError: (m: string) => void
}) {
  const [options, setOptions] = useState<any[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [rules, setRules] = useState<DayRule[]>([])
  const [pendingRules, setPendingRules] = useState<DayRule[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const api = scope === 'TRAINING_CENTRE' ? centersAPI : districtsAPI
    api.list().then((res) => setOptions(res.data.results || res.data)).catch(() => {})
  }, [scope])

  const loadForTarget = async (id: string) => {
    setSelectedId(id)
    if (!id) {
      setRules([])
      setPendingRules([])
      return
    }
    setIsLoading(true)
    try {
      const filters = scope === 'TRAINING_CENTRE' ? { scope, training_centre: id } : { scope, district: id }
      const res = await weekOffAPI.list(filters)
      const loaded = res.data.map((r: any) => ({ day_of_week: r.day_of_week, week_pattern: r.week_pattern }))
      setRules(loaded)
      setPendingRules(loaded)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load configuration.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!selectedId) return
    setIsSaving(true)
    try {
      const payload: any = { scope, rules: pendingRules }
      if (scope === 'TRAINING_CENTRE') payload.training_centre = selectedId
      else payload.district = selectedId
      await weekOffAPI.setScope(payload)
      success('Weekly-off configuration saved.')
      await loadForTarget(selectedId)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save weekly-off configuration.'))
    } finally {
      setIsSaving(false)
    }
  }

  const isCentre = scope === 'TRAINING_CENTRE'
  const label = isCentre ? 'Training Centre' : 'District'
  const ScopeIcon = isCentre ? Building2 : MapPin

  return (
    <div className="max-w-3xl">
      {/* Target picker */}
      <SectionCard>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
            <ScopeIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
              Select {label}
            </h2>
            <p className="text-xs text-neutral-500">
              Configure additional weekly offs for a specific {label.toLowerCase()}
            </p>
          </div>
        </div>

        <div className="relative group">
          <ScopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
          <select
            value={selectedId}
            onChange={(e) => loadForTarget(e.target.value)}
            className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
          >
            <option value="">Select {label}...</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      </SectionCard>

      {/* Configuration */}
      {isLoading ? (
        <div className="mt-4"><LoadingState /></div>
      ) : selectedId ? (
        <div className="mt-4">
          <SectionCard>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30 flex-shrink-0">
                <ScopeIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  {options.find((o) => String(o.id) === selectedId)?.name || label}
                </h2>
                <p className="text-xs text-neutral-500">Additional weekly offs configuration</p>
              </div>
            </div>

            <InfoBanner>
              Additional weekly offs for this {label.toLowerCase()} — these <strong className="font-semibold">add to</strong> the
              global configuration for every employee assigned here.
            </InfoBanner>

            <div className="mt-5">
              <WeekOffDayGrid initialRules={rules} onChange={setPendingRules} />
            </div>

            <div className="mt-6 pt-5 border-t border-neutral-100">
              <SaveButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : `Save ${label} Configuration`}
              </SaveButton>
            </div>
          </SectionCard>
        </div>
      ) : (
        <div className="mt-4 bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 py-16 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <ScopeIcon className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No {label.toLowerCase()} selected</p>
          <p className="text-xs text-neutral-400">
            Select a {label.toLowerCase()} above to configure its additional weekly offs.
          </p>
        </div>
      )}
    </div>
  )
}